import styled, { css } from 'styled-components'

import { colors } from 'app/theme/colors'
import { radius, shadow, space } from 'app/theme/tokens'

/**
 * Card primitives. Replaces the per-page `CardContainer` + `CardHeader` +
 * `CardBody` styled.divs in Attendance, Dashboard (Parent + Teacher
 * SummaryBlock), Inventory ProgramCards. Each previously had its own values
 * for box-shadow, border-radius, padding — now centralized.
 *
 *   <Card>
 *     <CardHeader>
 *       <CardTitle>Today's classes</CardTitle>
 *       <CardHeaderAction>...</CardHeaderAction>
 *     </CardHeader>
 *     <CardBody>{children}</CardBody>
 *     <CardFooter>...</CardFooter>
 *   </Card>
 *
 * `<Card tone="orange">` colours the header with the brand orange (matches
 * the existing Attendance CardHeader). Other tones can be added as the
 * design system grows — keep the prop API closed, not open-ended hex.
 */
const HEADER_TONES = {
  default: { bg: colors.ohOffWhite, fg: colors.textPrimary },
  orange: { bg: colors.ohOrange, fg: colors.white },
  blue: { bg: colors.ohBlue, fg: colors.white },
  green: { bg: colors.ohGreen, fg: colors.white }
}

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  border-radius: ${radius.md};
  box-shadow: ${shadow.default};
  overflow: hidden;

  ${(props) =>
    props.compact &&
    css`
      border-radius: ${radius.sm};
      box-shadow: ${shadow.sm};
    `}
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.sm};
  padding: ${space.md} ${space.lg};
  background: ${(props) =>
    (HEADER_TONES[props.tone] || HEADER_TONES.default).bg};
  color: ${(props) =>
    (HEADER_TONES[props.tone] || HEADER_TONES.default).fg};
  border-bottom: 1px solid ${colors.borderDefault};
`

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`

export const CardHeaderAction = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.xs};
`

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
  padding: ${space.lg};
  flex: 1 1 auto;
`

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${space.sm};
  padding: ${space.md} ${space.lg};
  border-top: 1px solid ${colors.borderDefault};
  background: ${colors.ohOffWhite};
`
